import { useCallback, useRef } from 'react';

type TypePosition = { x: number; y: number };

/**
 * [data-drag-handle] 영역 드래그로 패널 위치 이동
 * - data-drag-handle 속성이 있는 요소 내에서 시작된 클릭만 드래그 활성화
 * - viewport 경계를 벗어나지 않도록 clamp
 */
export const useDrag = (
  position: TypePosition,
  onPositionChange: (pos: TypePosition) => void,
  getSize: () => { width: number; height: number }
) => {
  const positionRef = useRef(position);
  positionRef.current = position;

  const getSizeRef = useRef(getSize);
  getSizeRef.current = getSize;

  const startRef = useRef<{ pointerX: number; pointerY: number; startX: number; startY: number } | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // resize가 먼저 처리된 경우 skip
    if (e.defaultPrevented) return;
    // [data-drag-handle] 내부 클릭만 드래그 시작
    const target = e.target as HTMLElement;
    if (!target.closest('[data-drag-handle]')) return;
    // 버튼 클릭은 드래그 무시
    if (target.closest('button')) return;

    e.currentTarget.setPointerCapture(e.pointerId);
    const pos = positionRef.current;
    startRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      startX: pos.x,
      startY: pos.y,
    };
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!startRef.current) return;

      const { pointerX, pointerY, startX, startY } = startRef.current;
      const { width, height } = getSizeRef.current();

      let nextX = startX + (e.clientX - pointerX);
      let nextY = startY + (e.clientY - pointerY);

      // viewport 경계 clamp
      nextX = Math.max(0, Math.min(nextX, window.innerWidth - width));
      nextY = Math.max(0, Math.min(nextY, window.innerHeight - height));

      onPositionChange({ x: nextX, y: nextY });
    },
    [onPositionChange]
  );

  const handlePointerUp = useCallback(() => {
    startRef.current = null;
  }, []);

  const handleLostPointerCapture = useCallback(() => {
    startRef.current = null;
  }, []);

  const isDragging = startRef;

  return {
    isDragging,
    dragProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onLostPointerCapture: handleLostPointerCapture,
    },
  };
};
